import { Box, Text, Flex } from '@mantine/core';
import { IconMapPin, IconMail, IconPhone } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ClientInfoText } from './ClientInfoText';
import { useClient } from 'src/clients/hooks/useClient';
import PageLoader from 'src/UI/loader/PageLoader';
import type { CalendarEvent } from 'src/meetings/models';
import { getAllClientParticipants } from '../utils/event-utils';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { useState } from 'react';

type ClientDetailsContentProps = {
  clientId: string;
};

const ClientDetailsContent = ({ clientId }: ClientDetailsContentProps) => {
  const { client, isLoading, error } = useClient(clientId);

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  if (error || !client) {
    return (
      <Text size="sm" c="red">
        Failed to load client details. Please try again.
      </Text>
    );
  }

  return (
    <>
      <ClientInfoText client={client} />

      <Flex direction="column" gap="lg">
        {client.personalDetails.address && (
          <Flex align="flex-start" gap="xxs">
            <IconMapPin
              size={16}
              stroke={1.5}
              color="var(--mantine-color-gray-7)"
              style={{ marginTop: '2px' }}
            />
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
              {client.personalDetails.address}
            </Text>
          </Flex>
        )}

        {client.emailAddress && (
          <Flex align="center" gap="xxs">
            <IconMail size={16} stroke={1.5} color="var(--mantine-color-gray-7)" />
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
              {client.emailAddress}
            </Text>
          </Flex>
        )}

        {client.personalDetails.phoneNumber && (
          <Flex align="center" gap="xxs">
            <IconPhone size={16} stroke={1.5} color="var(--mantine-color-gray-7)" />
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
              {client.personalDetails.phoneNumber}
            </Text>
          </Flex>
        )}
      </Flex>
    </>
  );
};

type Props = {
  event: CalendarEvent;
};

export const ClientDetailsCard = ({ event }: Props) => {
  const clientParticipants = getAllClientParticipants(event);
  const firstClientId = clientParticipants[0]?.participant.client?.id || '';
  const [selectedClientId, setSelectedClientId] = useState<string>(firstClientId);

  if (clientParticipants.length === 0 || !firstClientId) {
    return null;
  }

  if (clientParticipants.length === 1) {
    const clientId = clientParticipants[0].participant.client!.id;
    return (
      <Box bg="gray.1" p="lg" style={{ borderRadius: '8px' }}>
        <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="sm">
          Client details
        </Text>
        <ClientDetailsContent clientId={clientId} />
      </Box>
    );
  }

  const tabs = clientParticipants.map(participant => {
    const client = participant.participant.client!;
    return {
      value: client.id,
      label: `${client.firstName} ${client.lastName}`,
    };
  });

  return (
    <Box bg="gray.1" p="lg" style={{ borderRadius: '8px' }}>
      <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="sm">
        Client details
      </Text>
      <UnderlineTabs
        mode="local"
        variant="bordered"
        value={selectedClientId}
        onChange={setSelectedClientId}
        tabs={tabs}
      />
      <Box mt="md">
        <ClientDetailsContent clientId={selectedClientId} />
      </Box>
    </Box>
  );
};
